import { useEffect } from "react";
import {
  BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate,
} from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, setUnauthorizedHandler } from "./lib/api";
import { Shell } from "./components/Shell";
import { Login } from "./routes/Login";
import { Overview } from "./routes/Overview";
import { User } from "./routes/User";
import { Categories } from "./routes/Categories";
import { Ontology } from "./routes/Ontology";
import { Graph } from "./routes/Graph";
import { Quality } from "./routes/Quality";
import { Sources } from "./routes/Sources";
import { Reports } from "./routes/Reports";
import { MarginFinder } from "./routes/MarginFinder";
import { UserStatistics } from "./routes/UserStatistics";
import { Guide } from "./routes/Guide";

/* Os links `#u=<id>&tab=<aba>` já compartilhados continuam funcionando.
 * `replace: true` para a URL antiga não poluir o histórico. */
function LegacyHashRedirect() {
  const { hash } = useLocation();
  const navigate = useNavigate();
  useEffect(() => {
    if (!hash.startsWith("#u=")) return;
    const params = new URLSearchParams(hash.slice(1));
    const user = params.get("u");
    const tab = params.get("tab") ?? "user";
    const destino = tab === "geral" ? "/visao-geral"
                  : tab === "onto" ? `/ontologia/${user}`
                  : `/usuarios/${user}`;
    navigate(destino, { replace: true });
  }, [hash, navigate]);
  return null;
}

function Guard({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const session = useQuery({
    queryKey: ["session"],
    queryFn: () => api<{ authenticated: boolean }>("/api/auth/session"),
    retry: false,
  });

  useEffect(() => {
    setUnauthorizedHandler(() => {
      navigate(`/login?next=${encodeURIComponent(location.pathname + location.search)}`,
               { replace: true });
    });
  }, [navigate, location]);

  if (session.isPending) return null;
  if (!session.data?.authenticated) {
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  }
  return <>{children}</>;
}

export function App() {
  return (
    <BrowserRouter>
      <LegacyHashRedirect />
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route element={<Guard><Shell /></Guard>}>
          <Route index element={<Navigate to="/visao-geral" replace />} />
          <Route path="/visao-geral" element={<Overview />} />
          <Route path="/categorias" element={<Categories />} />
          <Route path="/usuarios/:userId?" element={<User />} />
          <Route path="/ontologia/:userId?" element={<Ontology />} />
          <Route path="/grafo/:userId?" element={<Graph />} />
          <Route path="/qualidade" element={<Quality />} />
          <Route path="/fontes" element={<Sources />} />
          <Route path="/relatorios" element={<Reports />} />
        <Route path="/margin-finder" element={<MarginFinder />} />
          <Route path="/user-statistics" element={<UserStatistics />} />
          <Route path="/guia" element={<Guide />} />
          <Route path="*" element={<Navigate to="/visao-geral" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}
